import { useCallback, useMemo } from "react";
import { atom, useRecoilTransaction_UNSTABLE, useRecoilValue } from "recoil";
import { AxiosError } from "axios";

import { Authentication } from "../../models";
import {
  State,
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../services";
import { SimpleSetValue } from "../../types";
import { useSetUserId } from "./userId";
import { useSetAuthToken } from "./authToken";

// Do NOT export atoms.
// This allows us to limit usage of these to only the hooks we export below.

const authenticationStatusAtom = atom<QueryStatus>({
  key: "[@fabric/core] authenticationStatusAtom",
  default: "idle",
});

const authenticationErrorAtom = atom<AxiosError | null>({
  key: "[@fabric/core] authenticationErrorAtom",
  default: null,
});

const authenticationDataAtom = atom<Authentication | undefined>({
  key: "[@fabric/core] authenticationDataAtom",
  default: undefined,
});

export const authenticationAccessError = new Error(
  "Attempted to access authentication data but it is not available",
);

// ##########################
// ## useSetAuthentication ##
// ##########################

export type ResultOfUseSetAuthentication = SimpleSetValue<
  State<Authentication>
>;
export type UseSetAuthentication = () => ResultOfUseSetAuthentication;

export const useSetAuthentication: UseSetAuthentication = () => {
  const setAuthToken = useSetAuthToken();
  const setUserId = useSetUserId();

  // Using useRecoilTransaction_UNSTABLE ensures that the atoms are updated atomically
  const setAtoms = useRecoilTransaction_UNSTABLE(
    ({ set }) =>
      (state: State<Authentication>) => {
        set(authenticationStatusAtom, state.status);
        set(authenticationErrorAtom, state.error);
        set(authenticationDataAtom, state.data);
      },
    [],
  );

  return useCallback(
    (state: State<Authentication>) => {
      setAtoms(state);
      setAuthToken(state.data?.identityToken);
      setUserId(state.data?.granularId);
    },
    [setAtoms, setAuthToken, setUserId],
  );
};

// #############################
// ## useAuthenticationStatus ##
// #############################

export type ResultOfUseAuthenticationStatus = QueryStatus;
export type UseAuthenticationStatus = () => ResultOfUseAuthenticationStatus;

export const useAuthenticationStatus: UseAuthenticationStatus = () => {
  return useRecoilValue(authenticationStatusAtom);
};

// ################################
// ## useAuthenticationError ##
// ################################

export type ResultOfUseAuthenticationError = AxiosError | null;
export type UseAuthenticationError = () => ResultOfUseAuthenticationError;

export const useAuthenticationError: UseAuthenticationError = () => {
  return useRecoilValue(authenticationErrorAtom);
};

// ################################
// ## useMaybeAuthenticationData ##
// ################################

export type ResultOfUseMaybeAuthenticationData = Authentication | undefined;
export type UseMaybeAuthenticationData =
  () => ResultOfUseMaybeAuthenticationData;

export const useMaybeAuthenticationData: UseMaybeAuthenticationData = () => {
  return useRecoilValue(authenticationDataAtom);
};

// ###########################
// ## useAuthenticationData ##
// ###########################

export type ResultOfUseAuthenticationData = Authentication;
export type UseAuthenticationData = () => ResultOfUseAuthenticationData;

export const useAuthenticationData: UseAuthenticationData = () => {
  const data = useMaybeAuthenticationData();
  if (!data) {
    throw authenticationAccessError;
  }
  return data;
};

// ############################
// ## useMaybeAuthentication ##
// ############################

export type ResultOfUseMaybeAuthentication = QueryEnvelope<Authentication>;
export type UseMaybeAuthentication = () => ResultOfUseMaybeAuthentication;

export const useMaybeAuthentication: UseMaybeAuthentication = () => {
  const status = useAuthenticationStatus();
  const error = useAuthenticationError();
  const data = useMaybeAuthenticationData();

  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// #######################
// ## useAuthentication ##
// #######################

export type ResultOfUseAuthentication = EnsuredQueryEnvelope<Authentication>;
export type UseAuthentication = () => ResultOfUseAuthentication;

export const useAuthentication: UseAuthentication = () => {
  const status = useAuthenticationStatus();
  const error = useAuthenticationError();
  const data = useAuthenticationData();

  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
